import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Lightbulb, Filter, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { value: '', label: 'Todas' },
  { value: 'ui', label: 'UI' },
  { value: 'batallas', label: 'Batallas' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'premios', label: 'Premios' },
  { value: 'reconocimientos', label: 'Reconocimientos' },
  { value: 'monetizacion', label: 'Monetización' },
  { value: 'otro', label: 'Otro' },
];

const STATUSES = [
  { value: '',            label: 'Todas',        color: '#FFFFFF' },
  { value: 'new',         label: 'Nueva',        color: '#00F0FF' },
  { value: 'reviewed',    label: 'Revisada',     color: '#B026FF' },
  { value: 'planned',     label: 'Planeada',     color: '#FFD166' },
  { value: 'in_progress', label: 'En progreso',  color: '#F97316' },
  { value: 'done',        label: 'Implementada', color: '#00FFA3' },
  { value: 'rejected',    label: 'Rechazada',    color: '#EF4444' },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminSuggestionsPage() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const { data } = await api.get('/admin/suggestions', { params });
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, filterStatus, filterCategory]);

  const updateStatus = async (sid, status) => {
    setSavingId(sid);
    try {
      await api.patch(`/admin/suggestions/${sid}`, { status });
      toast.success('Estado actualizado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setSavingId(null); }
  };

  const saveNotes = async (sid) => {
    setSavingId(sid);
    try {
      await api.patch(`/admin/suggestions/${sid}`, { admin_notes: notesDraft });
      toast.success('Nota guardada');
      setEditingId(null);
      setNotesDraft('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setSavingId(null); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-aura-bg grid place-items-center px-5 text-center">
        <div>
          <Lightbulb className="mx-auto text-aura-yellow mb-3" size={40} />
          <div className="font-display font-bold text-xl text-white mb-1">Solo Admin</div>
          <button onClick={() => nav('/home')} className="aura-btn-ghost mt-4">Volver</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg pb-16 relative" data-testid="admin-suggestions-page">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6 pb-2">
        <button onClick={() => nav('/admin')} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">SUGERENCIAS</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-4xl px-5 pt-4 space-y-5">
        <section className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="sug-stats">
          {STATUSES.filter(s => s.value).map(s => (
            <div key={s.value} className="aura-card p-3 text-center">
              <div className="text-[9px] uppercase tracking-widest text-aura-textDim">{s.label}</div>
              <div className="font-mono font-black text-xl mt-0.5" style={{ color: s.color }}>{stats[s.value] || 0}</div>
            </div>
          ))}
        </section>

        <section className="aura-card p-3 flex flex-wrap items-center gap-2 text-xs">
          <Filter size={14} className="text-aura-cyan" />
          <span className="text-aura-textDim mr-1">Filtros:</span>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-aura-surface2 border border-white/10 rounded-lg px-2 py-1 text-white text-xs"
          >
            {STATUSES.map(s => <option key={s.value || 'all'} value={s.value}>Estado: {s.label}</option>)}
          </select>
          <select
            data-testid="filter-category"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-aura-surface2 border border-white/10 rounded-lg px-2 py-1 text-white text-xs"
          >
            {CATEGORIES.map(c => <option key={c.value || 'all'} value={c.value}>Categoría: {c.label}</option>)}
          </select>
        </section>

        {loading ? (
          <div className="text-center py-20 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim" data-testid="sug-empty">
            No hay sugerencias con estos filtros.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-sug-list">
            {items.map((s) => {
              const statusMeta = STATUSES.find(x => x.value === s.status);
              const isEditing = editingId === s.id;
              return (
                <li key={s.id} className="aura-card p-4" data-testid={`admin-sug-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
                          style={{ color: statusMeta?.color, borderColor: `${statusMeta?.color}55` }}
                        >
                          {statusMeta?.label || s.status}
                        </span>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-aura-textDim">
                          {s.category}
                        </span>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/60">
                          prioridad {s.priority}
                        </span>
                      </div>
                      <div className="font-display font-black text-white">{s.title}</div>
                      <p className="text-xs text-aura-textDim mt-1 whitespace-pre-wrap">{s.description}</p>
                      <div className="mt-2 text-[10px] font-mono text-aura-textDim">
                        Por <span className="text-aura-cyan">@{s.organizer_username}</span> · {fmtDate(s.created_at)}
                      </div>
                    </div>
                  </div>

                  {s.admin_notes && !isEditing && (
                    <div className="mb-2 p-2.5 rounded-lg bg-aura-cyan/5 border border-aura-cyan/25 text-xs text-white/80">
                      <div className="text-[9px] uppercase tracking-widest text-aura-cyan mb-1">Mi nota</div>
                      {s.admin_notes}
                    </div>
                  )}

                  {isEditing && (
                    <div className="mb-2 space-y-2">
                      <textarea
                        data-testid={`notes-input-${s.id}`}
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Nota interna para el organizador…"
                        rows={2}
                        maxLength={1000}
                        className="w-full bg-aura-surface2 border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-aura-cyan/60"
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid={`save-notes-${s.id}`}
                          onClick={() => saveNotes(s.id)}
                          disabled={savingId === s.id}
                          className="aura-btn-primary !py-1.5 !px-3 text-xs flex-1 justify-center"
                        >
                          <Check size={12} /> Guardar nota
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setNotesDraft(''); }}
                          className="aura-btn-ghost !py-1.5 !px-3 text-xs"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
                    {STATUSES.filter(x => x.value).map(x => (
                      <button
                        key={x.value}
                        data-testid={`set-status-${s.id}-${x.value}`}
                        onClick={() => updateStatus(s.id, x.value)}
                        disabled={savingId === s.id || s.status === x.value}
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border transition"
                        style={{
                          color: s.status === x.value ? '#0F0F16' : x.color,
                          background: s.status === x.value ? x.color : 'transparent',
                          borderColor: `${x.color}66`,
                          opacity: savingId === s.id ? 0.5 : 1,
                        }}
                      >
                        {x.label}
                      </button>
                    ))}
                    {!isEditing && (
                      <button
                        data-testid={`edit-notes-${s.id}`}
                        onClick={() => { setEditingId(s.id); setNotesDraft(s.admin_notes || ''); }}
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 inline-flex items-center gap-1"
                      >
                        <MessageSquare size={11} /> {s.admin_notes ? 'Editar nota' : 'Añadir nota'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
